/**
 * CAT View Page
 *
 * Tela de visualização de uma CAT — segue a receita de `EmployeeDetailsPage`:
 * identidade → destaques → blocos temáticos.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Send as SendIcon,
  Cancel as CancelIcon,
  Print as PrintIcon,
  Person as PersonIcon,
  Business as CompanyIcon,
  ReportProblem as AccidentIcon,
  LocationOn as LocationIcon,
  LocalHospital as InjuryIcon,
  MedicalServices as TreatmentIcon,
  Badge as BadgeIcon,
  Sync as EsocialIcon,
  Notes as NotesIcon,
  Info as InfoIcon,
  Event as EventIcon,
  EventBusy as EventBusyIcon,
  Description as DescriptionIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageShell, IdentityBanner, IdentifierChip, SectionCard, InfoItem, StatCard, RouteActionButton, ActionButton } from '../../components';
import { pode } from '../../authorization/pode';
import { CatStatusChip, CatAccidentTypeChip } from './components';
import {
  Cat,
  formatCatDate,
  formatCatDateTime,
  canCatBeEdited,
  canCatBeCancelled,
  canCatBeSubmitted,
  CAT_REPORT_TYPE_LABELS,
  ACCIDENT_LOCATION_TYPE_LABELS,
  CAT_ISSUER_LABELS,
  AFFECTED_BODY_PART_LABELS,
  INJURY_NATURE_LABELS,
  LATERALITY_LABELS,
} from '../../types/cat';
import { catService } from '../../services/catService';
import { openSignedFile } from '../../services/fileAccess';

const getInitials = (name?: string): string =>
  (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();

const CatViewPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [cat, setCat] = useState<Cat | null>(null);
  const [loading, setLoading] = useState(true);

  // Cancel dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Submit dialog
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Print (#248)
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (id) {
      loadCat(parseInt(id, 10));
    }
  }, [id]);

  const loadCat = async (catId: number) => {
    setLoading(true);
    try {
      const data = await catService.getById(catId);
      setCat(data);
    } catch (err: any) {
      enqueueSnackbar('Erro ao carregar CAT', { variant: 'error' });
      navigate('/cats');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!cat) return;

    setCancelling(true);
    try {
      await catService.cancel(cat.id);
      enqueueSnackbar('CAT cancelada com sucesso', { variant: 'success' });
      setCancelDialogOpen(false);
      loadCat(cat.id);
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao cancelar CAT', { variant: 'error' });
    } finally {
      setCancelling(false);
    }
  };

  const handleSubmitConfirm = async () => {
    if (!cat) return;

    setSubmitting(true);
    try {
      await catService.submit(cat.id);
      enqueueSnackbar('CAT enviada com sucesso', { variant: 'success' });
      setSubmitDialogOpen(false);
      loadCat(cat.id);
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao enviar CAT', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = async () => {
    if (!cat) return;

    // Open the new tab synchronously inside the click handler so the popup
    // blocker does not swallow it after the async PDF + token round-trips.
    const target = window.open('about:blank', '_blank');
    if (!target) {
      enqueueSnackbar('Permita popups para visualizar o PDF', { variant: 'warning' });
      return;
    }

    setPrinting(true);
    try {
      const { pdfPath } = await catService.generatePdf(cat.id);
      await openSignedFile(pdfPath, target);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao gerar PDF';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setPrinting(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Início' },
    { label: 'Segurança do Trabalho' },
    { label: 'CAT' },
    { label: cat?.employee?.name || `CAT #${id}` },
  ];

  if (loading) {
    return (
      <PageShell
        breadcrumbs={breadcrumbItems}
        actions={
          <Button variant="outlined" size="small" startIcon={<BackIcon />} onClick={() => navigate('/cats')}>
            Voltar
          </Button>
        }
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!cat) {
    return null;
  }

  const companyName = cat.company?.tradeName || cat.company?.legalName;

  return (
    <PageShell
      breadcrumbs={breadcrumbItems}
      actions={
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<BackIcon />}
            onClick={() => navigate('/cats')}
          >
            Voltar
          </Button>
          <ActionButton
            acao="cat.read"
            rotulo="Imprimir"
            variant="outlined"
            size="small"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={printing}
          >
            {printing ? 'Gerando...' : 'Imprimir'}
          </ActionButton>
          {canCatBeEdited(cat.status) && (
            <RouteActionButton
              para="/cats/:id/edit"
              exige={pode('cat.update')}
              aoAgir={() => navigate(`/cats/${cat.id}/edit`)}
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
            >
              Editar
            </RouteActionButton>
          )}
          {canCatBeSubmitted(cat.status) && (
            <ActionButton
              acao="cat.update"
              rotulo="Enviar"
              variant="contained"
              size="small"
              color="primary"
              startIcon={<SendIcon />}
              onClick={() => setSubmitDialogOpen(true)}
            >
              Enviar
            </ActionButton>
          )}
          {canCatBeCancelled(cat.status) && (
            <ActionButton
              acao="cat.update"
              rotulo="Cancelar"
              variant="outlined"
              size="small"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => setCancelDialogOpen(true)}
            >
              Cancelar
            </ActionButton>
          )}
        </>
      }
    >
      {/* ═══ 1. Identidade ═══ */}
      <IdentityBanner
        avatar={getInitials(cat.employee?.name) || <PersonIcon sx={{ fontSize: 32 }} />}
        name={`CAT #${cat.id}`}
        color="#D32F2F"
        status={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <CatStatusChip status={cat.status} />
            <CatAccidentTypeChip type={cat.accidentType} />
          </Box>
        }
        context={`${companyName || '—'} › ${cat.employee?.name || '—'}`}
        identifiers={
          <>
            {cat.employee?.cpf && (
              <IdentifierChip icon={<BadgeIcon sx={{ fontSize: 16 }} />} label={cat.employee.cpf} />
            )}
            {cat.company?.cnpj && (
              <IdentifierChip icon={<CompanyIcon sx={{ fontSize: 16 }} />} label={cat.company.cnpj} />
            )}
          </>
        }
      />

      {/* ═══ 2. Destaques ═══ */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<EventIcon fontSize="small" />}
            label="Data do Acidente"
            value={formatCatDateTime(cat.accidentDate, cat.accidentTime ?? undefined)}
            color="#D32F2F"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<DescriptionIcon fontSize="small" />}
            label="Tipo de CAT"
            value={CAT_REPORT_TYPE_LABELS[cat.reportType] || '—'}
            color={theme.palette.info.main}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<EventBusyIcon fontSize="small" />}
            label="Afastamento"
            value={cat.willBeAbsent === 'S' ? 'Sim' : 'Não'}
            sublabel={cat.treatmentDuration ? `${cat.treatmentDuration} dias de tratamento` : undefined}
            color={theme.palette.warning.main}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<WarningIcon fontSize="small" />}
            label="Óbito"
            value={cat.isDeathReport === 'S' ? 'Sim' : 'Não'}
            color="#E53935"
          />
        </Grid>
      </Grid>

      {/* ═══ 3. Blocos temáticos ═══ */}
      <Grid container spacing={2.5}>
        {/* Left Column */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Grid container spacing={2.5}>
            {/* Employee Data */}
            <Grid size={12}>
              <SectionCard
                title="Dados do Funcionário"
                icon={<PersonIcon fontSize="small" />}
                accentColor={theme.palette.success.main}
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Funcionário" value={cat.employee?.name} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="CPF" value={cat.employee?.cpf} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      label="Data de Admissão"
                      value={cat.employee?.admissionDate ? formatCatDate(cat.employee.admissionDate) : ''}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Empresa" value={companyName} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="CNPJ" value={cat.company?.cnpj} />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>

            {/* Accident Data */}
            <Grid size={12}>
              <SectionCard
                title="Dados do Acidente"
                icon={<AccidentIcon fontSize="small" />}
                accentColor="#D32F2F"
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Tipo de CAT" value={CAT_REPORT_TYPE_LABELS[cat.reportType]} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Emissor" value={CAT_ISSUER_LABELS[cat.issuer]} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      label="Data do Acidente"
                      value={formatCatDateTime(cat.accidentDate, cat.accidentTime ?? undefined)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Horas Trabalhadas Antes" value={cat.hoursWorkedBefore} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Óbito" value={cat.isDeathReport === 'S' ? 'Sim' : 'Não'} />
                  </Grid>
                  <Grid size={12}>
                    <InfoItem label="Situação Geradora" value={cat.generatingSituation} fullWidth />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>

            {/* Accident Location */}
            <Grid size={12}>
              <SectionCard
                title="Local do Acidente"
                icon={<LocationIcon fontSize="small" />}
                accentColor="#D32F2F"
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      label="Tipo de Local"
                      value={ACCIDENT_LOCATION_TYPE_LABELS[cat.accidentLocationType]}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Código IBGE" value={cat.cityCode} />
                  </Grid>
                  <Grid size={12}>
                    <InfoItem
                      label="Endereço"
                      fullWidth
                      value={[
                        cat.streetType,
                        cat.streetName,
                        cat.streetNumber,
                        cat.complement,
                        cat.neighborhood,
                        cat.state,
                        cat.zipCode ? `CEP: ${cat.zipCode}` : '',
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>

            {/* Injury and Diagnosis */}
            <Grid size={12}>
              <SectionCard
                title="Lesão e Diagnóstico"
                icon={<InjuryIcon fontSize="small" />}
                accentColor="#E53935"
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      label="Parte do Corpo Afetada"
                      value={AFFECTED_BODY_PART_LABELS[cat.affectedBodyPart]}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      label="Lateralidade"
                      value={cat.laterality ? LATERALITY_LABELS[cat.laterality] : ''}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      label="Natureza da Lesão"
                      value={INJURY_NATURE_LABELS[cat.injuryNature]}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Agente Causador" value={cat.causingAgent} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Diagnóstico Provável" value={cat.probableDiagnosis} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      label="CID-10"
                      value={cat.cidCode ? `${cat.cidCode.code} - ${cat.cidCode.description}` : ''}
                    />
                  </Grid>
                  <Grid size={12}>
                    <InfoItem label="Descrição da Lesão" value={cat.injuryDescription} fullWidth />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>

            {/* Medical Treatment */}
            <Grid size={12}>
              <SectionCard
                title="Atendimento Médico"
                icon={<TreatmentIcon fontSize="small" />}
                accentColor="#E53935"
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      label="Data/Hora do Atendimento"
                      value={
                        cat.treatmentDate
                          ? formatCatDateTime(cat.treatmentDate, cat.treatmentTime ?? undefined)
                          : ''
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Internação" value={cat.wasHospitalized === 'S' ? 'Sim' : 'Não'} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      label="Duração do Tratamento"
                      value={cat.treatmentDuration ? `${cat.treatmentDuration} dias` : ''}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Afastamento" value={cat.willBeAbsent === 'S' ? 'Sim' : 'Não'} />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>
          </Grid>
        </Grid>

        {/* Right Column */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Grid container spacing={2.5}>
            {/* Issuer Professional */}
            <Grid size={12}>
              <SectionCard
                title="Profissional Emissor"
                icon={<BadgeIcon fontSize="small" />}
                accentColor="#FF8F00"
              >
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <InfoItem label="Nome" value={cat.issuerName} />
                  </Grid>
                  <Grid size={12}>
                    <InfoItem
                      label="Registro"
                      value={[cat.classEntity, cat.classEntityNumber, cat.classEntityState]
                        .filter(Boolean)
                        .join(' - ')}
                    />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>

            {/* eSocial */}
            <Grid size={12}>
              <SectionCard
                title="Integração eSocial"
                icon={<EsocialIcon fontSize="small" />}
                accentColor="#4527A0"
              >
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <InfoItem
                      label="Enviar para eSocial"
                      value={cat.sendToEsocial ? 'Sim' : 'Não'}
                    />
                  </Grid>
                  {cat.sendToEsocial && (
                    <>
                      <Grid size={12}>
                        <InfoItem
                          label="Ambiente"
                          value={
                            cat.esocialEnvironment === '1'
                              ? 'Produção'
                              : cat.esocialEnvironment === '2'
                              ? 'Produção Restrita'
                              : ''
                          }
                        />
                      </Grid>
                      <Grid size={12}>
                        <InfoItem label="ID do Evento" value={cat.esocialEventId} />
                      </Grid>
                      <Grid size={12}>
                        <InfoItem label="Status" value={cat.esocialStatus} />
                      </Grid>
                    </>
                  )}
                </Grid>
              </SectionCard>
            </Grid>

            {/* Notes */}
            {cat.notes && (
              <Grid size={12}>
                <SectionCard
                  title="Observações"
                  icon={<NotesIcon fontSize="small" />}
                  accentColor="#546E7A"
                >
                  <Typography variant="body2">{cat.notes}</Typography>
                </SectionCard>
              </Grid>
            )}

            {/* Metadata */}
            <Grid size={12}>
              <SectionCard
                title="Informações do Registro"
                icon={<InfoIcon fontSize="small" />}
                accentColor="#546E7A"
              >
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <InfoItem
                      label="Data de Emissão"
                      value={cat.issueDate ? formatCatDate(cat.issueDate) : ''}
                    />
                  </Grid>
                  <Grid size={12}>
                    <InfoItem label="Criado em" value={formatCatDate(cat.createdAt)} />
                  </Grid>
                  <Grid size={12}>
                    <InfoItem label="Atualizado em" value={formatCatDate(cat.updatedAt)} />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Confirmar Cancelamento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja cancelar esta CAT?
            Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
            Voltar
          </Button>
          <Button
            onClick={handleCancelConfirm}
            color="error"
            variant="contained"
            disabled={cancelling}
          >
            {cancelling ? 'Cancelando...' : 'Cancelar CAT'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submit Confirmation Dialog */}
      <Dialog open={submitDialogOpen} onClose={() => setSubmitDialogOpen(false)}>
        <DialogTitle>Confirmar Envio</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja enviar esta CAT?
            Após o envio, a CAT não poderá mais ser editada.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitDialogOpen(false)} disabled={submitting}>
            Voltar
          </Button>
          <Button
            onClick={handleSubmitConfirm}
            color="primary"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Enviando...' : 'Enviar CAT'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default CatViewPage;
